import {FatcaCrsCondition} from '@sollapay/enums';
import {useEffect, useMemo, useRef} from 'react';
import {useForm} from 'react-hook-form';

import {useSignMainFatcaCrsMutation} from '@/features/trust-accounts/activation/hooks';

import {
  EMPTY_FATCA_CRS_FORM_VALUES,
  toFatcaCrsFormValues,
  toSignMainFatcaCrsPayload
} from '../utils';

import type {FatcaCrsFormValues} from '../utils';
import type {FormStateSnapshot} from '@/hooks';
import type {FatcaCrsDeclarationDTO} from '@sollapay/types';

interface UseFatcaCrsFormParams {
  trustAccountId: string;
  declaration: FatcaCrsDeclarationDTO | null | undefined;
  isLoading: boolean;
  isError: boolean;
  onFormStateChange?: (state: FormStateSnapshot) => void;
}

export const useFatcaCrsForm = ({
  trustAccountId,
  declaration,
  isLoading,
  isError,
  onFormStateChange
}: UseFatcaCrsFormParams) => {
  const signMainMutation = useSignMainFatcaCrsMutation(trustAccountId);

  const form = useForm<FatcaCrsFormValues>({
    defaultValues: EMPTY_FATCA_CRS_FORM_VALUES
  });

  const declarationDefaults = useMemo(() => toFatcaCrsFormValues(declaration), [declaration]);

  useEffect(() => {
    form.reset(declarationDefaults);
  }, [form, declarationDefaults]);

  const handleSave = form.handleSubmit(async values => {
    const result = await signMainMutation.mutateAsync(toSignMainFatcaCrsPayload(values));
    form.reset(toFatcaCrsFormValues(result.data.declaration));
  });

  const handleCancel = () => {
    form.reset(declarationDefaults);
    signMainMutation.reset();
  };

  const isDirty = form.formState.isDirty;
  const isSaving = signMainMutation.isPending;

  const conditions = form.watch('conditions');
  const consentDisclosure = form.watch('consentDisclosure');
  const consentAccuracyUpdate = form.watch('consentAccuracyUpdate');
  const consentSignature = form.watch('consentSignature');

  const allConditions = Object.values(FatcaCrsCondition);
  const hasErrors =
    !allConditions.every(c => conditions.includes(c)) ||
    !consentDisclosure ||
    !consentAccuracyUpdate ||
    !consentSignature;

  const onSaveRef = useRef(handleSave);
  onSaveRef.current = handleSave;
  const onCancelRef = useRef(handleCancel);
  onCancelRef.current = handleCancel;

  useEffect(() => {
    onFormStateChange?.({
      isDirty,
      isLoading,
      isSaving,
      isError,
      hasErrors,
      onSave: () => onSaveRef.current(),
      onCancel: () => onCancelRef.current()
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, isLoading, isSaving, isError, hasErrors]);

  return {
    form,
    handleSave,
    handleCancel,
    isDirty,
    isSaving,
    isLoading,
    isError,
    saveError: signMainMutation.error
  };
};
